import { useState, useCallback } from "react";
import dayjs from "dayjs";
import { orgToday } from "@/lib/tz";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { RichTextEditor, sanitizeHtml } from "@/components/RichTextEditor";
import { toast } from "sonner";
import {
  Loader2, Sparkles, FileText, Copy, Check, RefreshCw, ChevronLeft,
  Bug, Lightbulb, Newspaper,
} from "lucide-react";

const BRAND_BLUE = "#1B65D4";
const PRIORITY_STYLE = {
  Critical: "bg-red-100 text-red-800 border-red-300",
  High:     "bg-orange-50 text-orange-700 border-orange-200",
  Medium:   "bg-amber-50 text-amber-700 border-amber-200",
  Low:      "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_STYLE = {
  "New":                    "bg-sky-50 text-sky-700 border-sky-200",
  "In Progress":            "bg-violet-50 text-violet-700 border-violet-200",
  "Waiting to be Deployed": "bg-amber-50 text-amber-700 border-amber-200",
  "Resolved":               "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function today()    { return orgToday(); }
function monthAgo() { return dayjs(orgToday()).subtract(1, "month").format("YYYY-MM-DD"); }
function issueTitle(issue) {
  return issue.ai?.title || issue.what_happened || issue.description || issue.issue_id;
}


// ─── Scope presets ────────────────────────────────────────────────────────────
const SCOPES = [
  {
    id: "resolved",
    label: "Resolved only",
    description: "Only fully resolved issues — clean and ready to send.",
    statuses: ["Resolved"],
  },
  {
    id: "full",
    label: "Full update",
    description: "Resolved + waiting to deploy + in progress — great for weekly status emails.",
    statuses: ["Resolved", "Waiting to be Deployed", "In Progress"],
  },
  {
    id: "everything",
    label: "Everything",
    description: "All issues including newly reported — full audit view.",
    statuses: ["Resolved", "Waiting to be Deployed", "In Progress", "New"],
  },
];

// ─── Phase 1 — date range ─────────────────────────────────────────────────────
function Phase1({ dateFrom, dateTo, setDateFrom, setDateTo, scope, setScope, onGenerate, generating, progressMsg }) {
  const canGenerate = dateFrom && dateTo && dateFrom <= dateTo;
  return (
    <div className="space-y-5 py-2" data-testid="rn-phase1">
      <p className="text-xs text-slate-500 leading-relaxed">
        Select a date range and report scope. The AI will draft a release notes email
        you can edit, refine, and export.
      </p>

      {/* Date pickers */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">From</Label>
          <Input type="date" value={dateFrom} max={dateTo || today()} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-sm" data-testid="rn-date-from" />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">To</Label>
          <Input type="date" value={dateTo} min={dateFrom || undefined} max={today()} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-sm" data-testid="rn-date-to" />
        </div>
      </div>

      {/* Scope selector */}
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-2 block">
          Report Scope
        </Label>
        <div className="space-y-2">
          {SCOPES.map((s) => {
            const active = scope === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setScope(s.id)}
                data-testid={`rn-scope-${s.id}`}
                className={`w-full text-left rounded-lg border px-3.5 py-2.5 transition-all ${
                  active
                    ? "border-blue-400 bg-blue-50 ring-1 ring-blue-300"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`h-3.5 w-3.5 rounded-full border-2 flex-shrink-0 ${
                    active ? "border-blue-500 bg-blue-500" : "border-slate-300 bg-white"
                  }`} />
                  <span className={`text-xs font-semibold ${active ? "text-blue-700" : "text-slate-700"}`}>
                    {s.label}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5 ml-5.5 pl-[22px]">
                  {s.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <Button className="w-full h-9 text-sm font-medium" style={{ backgroundColor: BRAND_BLUE }} onClick={onGenerate} disabled={!canGenerate || generating} data-testid="rn-generate-btn">
        {generating
          ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />{progressMsg || "Generating draft…"}</>
          : <><Sparkles className="h-3.5 w-3.5 mr-2" />Generate Draft</>}
      </Button>
    </div>
  );
}

// ─── Issue row in exclusion panel ────────────────────────────────────────────
function IssueRow({ issue, excluded, onToggle }) {
  const tid = issue.issue_id;
  return (
    <label htmlFor={`rn-exc-${tid}`}
      className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition-colors ${excluded ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white hover:border-slate-300"}`}
      data-testid={`rn-issue-row-${tid}`}>
      <Checkbox id={`rn-exc-${tid}`} checked={excluded} onCheckedChange={() => onToggle(tid)} className="mt-0.5 shrink-0" data-testid={`rn-exc-${tid}`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1 mb-0.5">
          <span className="text-[10px] font-mono text-slate-400">{tid}</span>
          {issue.type === "Bug"
            ? <Badge className="text-[9px] py-0 bg-rose-50 text-rose-700 border border-rose-200"><Bug className="h-2.5 w-2.5 mr-0.5" />Bug</Badge>
            : <Badge className="text-[9px] py-0 bg-indigo-50 text-indigo-700 border border-indigo-200"><Lightbulb className="h-2.5 w-2.5 mr-0.5" />Request</Badge>}
          {issue.priority && <Badge className={`text-[9px] py-0 border ${PRIORITY_STYLE[issue.priority] || ""}`}>{issue.priority}</Badge>}
          {issue.status   && <Badge className={`text-[9px] py-0 border ${STATUS_STYLE[issue.status]   || ""}`}>{issue.status}</Badge>}
        </div>
        <p className="text-[11px] text-slate-700 leading-snug line-clamp-2">{issueTitle(issue)}</p>
      </div>
    </label>
  );
}

// ─── Phase 2 — review / edit ─────────────────────────────────────────────────
function Phase2({
  issues, excludedIds, onToggleExclude,
  draftHtml, setDraftHtml,
  onRegenerate, regenerating, progressMsg,
  feedback, setFeedback,
  onRefine, refining,
  onCopy, copied,
  onExportPdf, exporting,
  onBack,
  dateFrom, dateTo, includedCount,
}) {
  return (
    <div className="flex flex-col h-full" data-testid="rn-phase2">
      {/* top action bar */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-100 bg-white shrink-0 gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors shrink-0" data-testid="rn-back-btn">
          <ChevronLeft className="h-3.5 w-3.5" />Back
        </button>
        <span className="text-[11px] text-slate-400 tabular-nums hidden sm:block">
          {dateFrom} → {dateTo} &nbsp;·&nbsp; <span className="font-medium text-slate-600">{includedCount} issue{includedCount !== 1 ? "s" : ""} included</span>
        </span>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" onClick={onCopy} data-testid="rn-copy-btn">
            {copied ? <><Check className="h-3.5 w-3.5 text-emerald-600" />Copied!</> : <><Copy className="h-3.5 w-3.5" />Copy</>}
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" onClick={onExportPdf} disabled={exporting} data-testid="rn-export-pdf-btn">
            {exporting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Exporting…</> : <><FileText className="h-3.5 w-3.5" />Export PDF</>}
          </Button>
        </div>
      </div>

      {/* two-column body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* left — issue exclusion list */}
        <div className="w-60 xl:w-72 shrink-0 flex flex-col border-r border-slate-100 bg-slate-50/50">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Issues ({issues.length})</span>
            <span className="text-[10px] text-slate-400">✓ = exclude</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="rn-issue-list">
            {issues.map((issue) => (
              <IssueRow key={issue.issue_id} issue={issue} excluded={excludedIds.includes(issue.issue_id)} onToggle={onToggleExclude} />
            ))}
          </div>
          <div className="px-3 py-2.5 border-t border-slate-100 bg-white shrink-0 space-y-1.5">
            <Button size="sm" variant="outline" className="w-full h-7 text-xs gap-1.5" onClick={onRegenerate} disabled={regenerating || includedCount === 0} data-testid="rn-regenerate-btn">
              {regenerating ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />{progressMsg || "Regenerating…"}</> : <><RefreshCw className="h-3.5 w-3.5" />Regenerate</>}
            </Button>
            {excludedIds.length > 0 && (
              <p className="text-[10px] text-amber-600 text-center">{excludedIds.length} issue{excludedIds.length !== 1 ? "s" : ""} excluded — click Regenerate to apply</p>
            )}
          </div>
        </div>

        {/* right — editor + AI feedback strip */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4">
            <RichTextEditor value={draftHtml} onChange={setDraftHtml} headings links placeholder="AI draft will appear here…" testid="rn-editor" />
          </div>
          {/* AI feedback loop */}
          <div className="shrink-0 border-t border-slate-100 bg-slate-50/70 px-4 py-3">
            <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">
              <Sparkles className="h-3 w-3 inline mr-1 text-violet-500" />Ask AI to revise
            </Label>
            <div className="flex gap-2 items-end">
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !refining && feedback.trim()) {
                    e.preventDefault();
                    onRefine();
                  }
                }}
                placeholder="e.g. Make the tone more formal, add a subject line, focus on resolved bugs only… (Shift+Enter for new line, Enter to send)"
                className="text-xs flex-1 min-h-[36px] max-h-32 resize-none"
                rows={2}
                disabled={refining}
                data-testid="rn-feedback-input"
              />
              <Button size="sm" className="h-8 text-xs px-3 gap-1.5 shrink-0 mb-0.5" style={{ backgroundColor: BRAND_BLUE }} onClick={onRefine} disabled={refining || !feedback.trim()} data-testid="rn-refine-btn">
                {refining ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Revising…</> : <><Sparkles className="h-3.5 w-3.5" />Revise</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Main exported dialog ────────────────────────────────────────────────────
export function ReleaseNotesDialog({ open, onOpenChange }) {
  const [dateFrom,      setDateFrom]      = useState(monthAgo);
  const [dateTo,        setDateTo]        = useState(today);
  const [scope,         setScope]         = useState("resolved");
  const [generating,    setGenerating]    = useState(false);
  const [progressMsg,   setProgressMsg]   = useState("");
  const [phase,         setPhase]         = useState(1);
  const [issues,        setIssues]        = useState([]);
  const [excludedIds,   setExcludedIds]   = useState([]);
  const [draftHtml,     setDraftHtml]     = useState("");
  const [includedCount, setIncludedCount] = useState(0);
  const [modelUsed,     setModelUsed]     = useState(null);
  const [regenerating,  setRegenerating]  = useState(false);
  const [feedback,      setFeedback]      = useState("");
  const [refining,      setRefining]      = useState(false);
  const [copied,        setCopied]        = useState(false);
  const [exporting,     setExporting]     = useState(false);

  const runGenerate = useCallback(async (excluded) => {
    const isRegen = phase === 2;
    if (isRegen) setRegenerating(true); else setGenerating(true);
    setProgressMsg("Preparing…");
    try {
      const BASE = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("token");
      const resp = await fetch(`${BASE}/api/admin/release-notes/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          date_from:    dateFrom,
          date_to:      dateTo,
          excluded_ids: excluded,
          statuses:     SCOPES.find((s) => s.id === scope)?.statuses || ["Resolved"],
          model:        modelUsed || undefined,
        }),
      });

      if (!resp.ok) {
        // Non-streaming error (validation, 404, etc.)
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.detail || `Server error (${resp.status})`);
      }

      // Read SSE stream
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // keep incomplete line in buffer
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));

          if (data.step === "error") {
            throw new Error(data.detail || "AI generation failed.");
          }
          if (data.step === "done") {
            setDraftHtml(sanitizeHtml(data.draft_html));
            setIssues(data.issues || []);
            setIncludedCount(data.included_count ?? 0);
            setModelUsed(data.model_used || null);
            setPhase(2);
          } else {
            setProgressMsg(data.detail || "Working…");
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "Failed to generate release notes.");
    } finally {
      if (isRegen) setRegenerating(false); else setGenerating(false);
      setProgressMsg("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, modelUsed, phase, scope]);

  const handleGenerate   = () => runGenerate(excludedIds);
  const handleRegenerate = () => runGenerate(excludedIds);

  const handleToggleExclude = useCallback((id) => {
    setExcludedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);

  const handleRefine = useCallback(async () => {
    if (!feedback.trim()) return;
    setRefining(true);
    try {
      const res = await api.releaseNotesRefine({
        draft_html:   draftHtml,
        feedback:     feedback.trim(),
        issue_ids:    issues.map((i) => i.issue_id),
        excluded_ids: excludedIds,
        model:        modelUsed || undefined,
      });
      setDraftHtml(sanitizeHtml(res.draft_html));
      setFeedback("");
      toast.success("Draft revised by AI.");
    } catch (e) {
      toast.error(getApiError(e, "AI revision failed."));
    } finally {
      setRefining(false);
    }
  }, [draftHtml, feedback, issues, excludedIds, modelUsed]);

  const handleCopy = useCallback(async () => {
    const tmp = document.createElement("div");
    tmp.innerHTML = draftHtml;
    const plain = tmp.innerText || tmp.textContent || "";
    try {
      await navigator.clipboard.writeText(plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Clipboard access denied — select and copy manually.");
    }
  }, [draftHtml]);

  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    try {
      const resp = await api.releaseNotesPdf({ html_content: draftHtml, date_from: dateFrom, date_to: dateTo });
      const blob = resp instanceof Blob ? resp : resp.data;
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `release-notes-${dateFrom}_to_${dateTo}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error(getApiError(e, "PDF export failed."));
    } finally {
      setExporting(false);
    }
  }, [draftHtml, dateFrom, dateTo]);

  const handleOpenChange = (val) => {
    if (!val) { setPhase(1); setIssues([]); setExcludedIds([]); setDraftHtml(""); setFeedback(""); setCopied(false); setScope("resolved"); setProgressMsg(""); }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`flex flex-col overflow-hidden p-0 gap-0 ${phase === 2 ? "sm:max-w-5xl h-[88vh]" : "sm:max-w-md"}`}
        data-testid="rn-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 shrink-0">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Newspaper className="h-4 w-4" style={{ color: BRAND_BLUE }} />
            Release Notes Generator
          </DialogTitle>
        </DialogHeader>

        {phase === 1 ? (
          <div className="px-5 pb-5">
            <Phase1
              dateFrom={dateFrom} dateTo={dateTo}
              setDateFrom={setDateFrom} setDateTo={setDateTo}
              scope={scope} setScope={setScope}
              onGenerate={handleGenerate} generating={generating}
              progressMsg={progressMsg}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-hidden">
            <Phase2
              issues={issues} excludedIds={excludedIds} onToggleExclude={handleToggleExclude}
              draftHtml={draftHtml} setDraftHtml={setDraftHtml}
              onRegenerate={handleRegenerate} regenerating={regenerating} progressMsg={progressMsg}
              feedback={feedback} setFeedback={setFeedback}
              onRefine={handleRefine} refining={refining}
              onCopy={handleCopy} copied={copied}
              onExportPdf={handleExportPdf} exporting={exporting}
              onBack={() => setPhase(1)}
              dateFrom={dateFrom} dateTo={dateTo} includedCount={includedCount}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

